/**
 * `SessionExpiryWarning` — shared capsule that surfaces a countdown dialog
 * 1 minute before the authenticated user's access token expires.
 *
 * Rendered once per authenticated surface (inside `CustomerLayout` in the
 * Storefront and `AdminLayout` in the Console), this component:
 *
 *   1. Subscribes to `state.auth.isAuthenticated` and `state.auth.expiresAt`
 *      via `useAppSelector`.
 *   2. Ticks a 1s `setInterval` that recomputes the remaining time.
 *   3. When `expiresAt - Date.now() <= 60_000`, opens a shadcn `Dialog`
 *      showing "Your session expires in MM:SS. Sign in again to continue."
 *   4. Offers two actions:
 *        • "Sign in again" (primary) — calls `useLogout` (SF-AUTH-009)
 *          which revokes server-side, clears tokens, resets Redux, and
 *          navigates to `/login`.
 *        • "Dismiss" (secondary) — hides the dialog for a single tick;
 *          it re-opens next second if the window is still ≤ 60s out.
 *   5. Auto-closes when a heartbeat pushes `expiresAt` more than 60s into
 *      the future.
 *
 * Not rendered while unauthenticated — returns `null` before the dialog
 * has a chance to mount.
 *
 * SF-AUTH-008.
 */
import * as React from 'react';

import { Button } from '@dfq/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@dfq/shared/components/ui/dialog';
import { useLogout } from '@dfq/shared/hooks';
import { cn } from '@dfq/shared/lib/utils';
import { useAppSelector } from '@dfq/shared/stores/hooks';

/** Milliseconds before expiry at which the warning dialog opens. */
export const SESSION_WARNING_THRESHOLD_MS = 60_000;

/** How often the countdown re-evaluates. */
export const SESSION_WARNING_TICK_MS = 1_000;

export interface SessionExpiryWarningProps extends Omit<React.ComponentProps<typeof DialogContent>, 'children'> {
  /**
   * Optional class name merged onto the dialog content surface via `cn()`.
   * Use for layout tweaks at the call site — the capsule owns its own
   * visual defaults.
   */
  className?: string;
}

/**
 * Format a millisecond duration as `MM:SS`. Negative / zero values clamp
 * to `00:00` so the dialog never displays "-0:01" during the final tick.
 */
function formatCountdown(remainingMs: number): string {
  const clamped = Math.max(0, remainingMs);
  const totalSeconds = Math.ceil(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * Countdown dialog that triggers 1 minute before the current session
 * expires. Takes no props beyond an optional `className` for the dialog
 * surface — all state comes from the auth slice.
 */
export function SessionExpiryWarning({ className, ...dialogContentProps }: SessionExpiryWarningProps = {}) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const expiresAt = useAppSelector((state) => state.auth.expiresAt);
  const logout = useLogout();

  // `now` drives the countdown. We re-read `Date.now()` on each tick so the
  // value stays aligned with the server-provided `expiresAt` even if the
  // tab was backgrounded briefly.
  const [now, setNow] = React.useState<number>(() => Date.now());

  // `dismissedAtTickNow` is the `now` value at which the user most
  // recently dismissed the dialog. The dialog stays hidden only while the
  // current `now` equals that tick — as soon as the 1s interval updates
  // `now`, the equality fails and the dialog can re-open. This makes
  // dismissal "one tick" without needing a second `useEffect` to reset
  // the flag. A fresh `expiresAt` (heartbeat refresh) pushes the window
  // beyond 60s and auto-hides the dialog regardless of dismissal state.
  const [dismissedAtTickNow, setDismissedAtTickNow] = React.useState<number | null>(null);

  // 1s interval. Paused while unauthenticated — we don't need to poll a
  // timer that will never match.
  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, SESSION_WARNING_TICK_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated]);

  const remainingMs = React.useMemo(() => {
    if (expiresAt === null) return Number.POSITIVE_INFINITY;
    return expiresAt - now;
  }, [expiresAt, now]);

  const handleDismiss = React.useCallback(() => {
    setDismissedAtTickNow(now);
  }, [now]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        handleDismiss();
      }
    },
    [handleDismiss],
  );

  const handleSignInAgain = React.useCallback(async () => {
    await logout();
  }, [logout]);

  const withinWindow = isAuthenticated && expiresAt !== null && remainingMs <= SESSION_WARNING_THRESHOLD_MS;
  const isDismissed = dismissedAtTickNow !== null && dismissedAtTickNow === now;
  const open = withinWindow && !isDismissed;

  // If the user isn't authenticated we render nothing at all — avoids
  // mounting a Radix portal just to hold a `open={false}` state.
  if (!isAuthenticated) {
    return null;
  }

  const countdownLabel = formatCountdown(remainingMs);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="session-expiry-warning"
        showCloseButton={false}
        className={cn('sm:max-w-sm', className)}
        {...dialogContentProps}
      >
        <DialogHeader>
          <DialogTitle>Your session is about to expire</DialogTitle>
          <DialogDescription>
            <span aria-live="polite" aria-atomic="true">
              Your session expires in{' '}
              <span data-testid="session-expiry-warning-countdown" className="text-foreground font-medium tabular-nums">
                {countdownLabel}
              </span>
              . Sign in again to continue.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={handleDismiss}>
            Dismiss
          </Button>
          <Button variant="default" type="button" onClick={handleSignInAgain}>
            Sign in again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
